import React from 'react';
import { McTable, McTag, McIcon, McLoadingIndicator, McButton } from '@maersk-global/mds-react-wrapper/components-core';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { FavoriteButton } from './FavoriteButton';
import { queryService } from '../../services/queryService';

interface QueryTableProps {
    queries: any[];
    totalElements: number;
    isLoading: boolean;
    isFetchingNextPage: boolean;
    selectedQueryId: string | null;
    searchQuery: string;
    onSelectQuery: (queryId: string) => void;
    onNavigate: (path: string) => void;
    observerRef?: React.RefObject<any>;
}

export const QueryTable: React.FC<QueryTableProps> = ({
    queries,
    totalElements,
    isLoading,
    isFetchingNextPage,
    selectedQueryId,
    searchQuery,
    onSelectQuery,
    onNavigate,
    observerRef
}) => {
    const [copyingId, setCopyingId] = React.useState<string | null>(null);

    const handleCopySQL = async (queryId: string) => {
        setCopyingId(queryId);
        try {
            const fullQuery = await queryService.getQuery(queryId);
            if (fullQuery?.sqlContent) {
                await navigator.clipboard.writeText(fullQuery.sqlContent);
            }
        } catch {
            // silent fail
        } finally {
            setTimeout(() => setCopyingId(null), 1500);
        }
    };

    return (
        <div style={{ padding: 0 }}>
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                    <McLoadingIndicator />
                </div>
            ) : queries && queries.length > 0 ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', paddingBottom: '8px' }}>
                        <span className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)' }}>
                            Showing {queries.length} of {totalElements} queries{searchQuery.trim() ? ` matching "${searchQuery}"` : ''}
                        </span>
                        <span className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>
                            Click ID to view details.
                        </span>
                    </div>
                    <div style={{ maxHeight: '600px', overflow: 'auto' }}>
                        <McTable
                            data={queries.map(q => ({ ...q, id: q.queryId, status: (q.active ?? q.isActive) ? 'Active' : 'Inactive' }))}
                            columns={[
                                { id: 'favorite', label: '' },
                                { id: 'queryId', label: 'Query ID' },
                                { id: 'name', label: 'Name' },
                                { id: 'applicationName', label: 'Application' },
                                { id: 'category', label: 'Category' },
                                { id: 'tags', label: 'Tags' },
                                { id: 'currentVersion', label: 'Version' },
                                { id: 'status', label: 'Status' },
                                { id: 'overrides', label: 'Overrides' },
                                { id: 'actions', label: '' }
                            ]}
                            datakey="id"
                        >
                            {queries.map((query: any) => (
                                <React.Fragment key={query.queryId}>
                                    <div slot={`${query.queryId}_favorite`}>
                                        <FavoriteButton
                                            queryId={query.queryId}
                                            isFavorite={query.isFavorite || false}
                                            size="sm"
                                        />
                                    </div>
                                    <div
                                        slot={`${query.queryId}_queryId`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onNavigate(`/queries/${query.queryId}`);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--mds-color-primary-main-text)', textDecoration: 'underline' }}
                                    >
                                        {query.queryId}
                                        <McIcon icon="square-arrow-up-right" size="16" />
                                    </div>
                                    <div slot={`${query.queryId}_applicationName`}>
                                        {query.applicationName
                                            ? query.applicationName
                                            : <span style={{ color: 'var(--mds-color-neutral-weaker-text)', fontSize: '12px' }}>–</span>}
                                    </div>
                                    <div slot={`${query.queryId}_category`}>
                                        <McTag label={query.category || 'N/A'} />
                                    </div>
                                    <div slot={`${query.queryId}_tags`} style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '300px' }}>
                                        {query.tags && query.tags.length > 0 ? (
                                            query.tags.map((tag: string) => (
                                                <McTag
                                                    key={tag}
                                                    label={tag}
                                                    appearance={
                                                        tag === 'Tech Support' ? 'info' :
                                                            tag === 'Sentinel' ? 'success' :
                                                                tag === 'Application' ? 'warning' :
                                                                    'neutral'
                                                    }
                                                />
                                            ))
                                        ) : (
                                            <span style={{ color: 'var(--mds-color-neutral-weaker-text)', fontSize: '12px' }}>-</span>
                                        )}
                                    </div>
                                    <div slot={`${query.queryId}_status`}>
                                        <McTag
                                            label={(query.active ?? query.isActive) ? 'Active' : 'Inactive'}
                                            appearance={(query.active ?? query.isActive) ? 'success' : 'neutral'}
                                        />
                                    </div>
                                    <div slot={`${query.queryId}_overrides`}>
                                        {query.overrideTerminals && query.overrideTerminals.length > 0 ? (
                                            <McTooltip
                                                onMouseOver={(e: any) => { e.currentTarget.open = true; }}
                                                onMouseOut={(e: any) => { e.currentTarget.open = false; }}
                                                position="bottom-center"
                                            >
                                                <div slot="trigger" style={{ display: 'inline-block', cursor: 'default' }}>
                                                    <McTag
                                                        label={`⚡ ${query.overrideTerminals.length}`}
                                                        appearance="warning"
                                                        fit="small"
                                                    />
                                                </div>
                                                <div slot="content" style={{ maxHeight: '200px', overflowY: 'auto', minWidth: '120px' }}>
                                                    {query.overrideTerminals.map((t: string) => (
                                                        <div key={t} style={{ padding: '4px 0', fontSize: '12px' }}>{t}</div>
                                                    ))}
                                                </div>
                                            </McTooltip>
                                        ) : (
                                            <span style={{ color: 'var(--mds-color-neutral-weaker-text)', fontSize: '12px' }}>–</span>
                                        )}
                                    </div>
                                    <div slot={`${query.queryId}_actions`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                        <McTooltip position="top-center">
                                            <McButton
                                                slot="trigger"
                                                fit="small"
                                                variant="plain"
                                                onClick={() => onNavigate(`/queries/${query.queryId}`)}
                                            >
                                                <McIcon icon="eye" slot="prefix" />
                                            </McButton>
                                            <span slot="content">Open query overview</span>
                                        </McTooltip>
                                        <McTooltip position="top-center">
                                            <McButton
                                                slot="trigger"
                                                fit="small"
                                                variant="plain"
                                                disabled={copyingId === query.queryId}
                                                onClick={() => handleCopySQL(query.queryId)}
                                            >
                                                <McIcon icon={copyingId === query.queryId ? 'check' : 'copy'} slot="prefix" />
                                            </McButton>
                                            <span slot="content">{copyingId === query.queryId ? 'Copied!' : 'Copy SQL to editor'}</span>
                                        </McTooltip>
                                    </div>
                                </React.Fragment>
                            ))}
                        </McTable>
                    </div>
                </>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                    <span className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)' }}>
                        No queries found. Create your first query to get started.
                    </span>
                </div>
            )}
            {isFetchingNextPage && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                    <McLoadingIndicator />
                </div>
            )}
            <div ref={observerRef} style={{ height: '20px' }} />
        </div>
    );
};
